import React, { useEffect, useState } from 'react';
import { getCategories, getProducts } from '../dataManager';
import aboutus from "../assets/hero_img_page.png";
import useBidirectionalScroll from '../hooks/useBidirectionalScroll';
import ProductItemCard from './ProductItemCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCogs } from '@fortawesome/free-solid-svg-icons';
import Loader from './Loader';

export default function ProductsPage({ currentCategory, navigateTo }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const categories = getCategories();
  const products = getProducts();

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth) - 0.5,
        y: (e.clientY / window.innerHeight) - 0.5
      });
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    if (currentCategory) {
      setTimeout(() => {
        const el = document.getElementById(currentCategory);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [currentCategory]);

  useBidirectionalScroll('.products-page-container', 120);

  return (
    <div className="products-page-container" style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
      <section className="hero" style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 0,
          pointerEvents: 'none',
          overflow: 'hidden'
        }}>
          <div style={{
            fontSize: 'min(20vw, 250px)',
            fontWeight: 900,
            color: 'transparent',
            WebkitTextStroke: '2px rgba(10, 102, 255, 0.08)',
            letterSpacing: '10px',
            transform: `translate(${mousePos.x * 50}px, ${mousePos.y * 50}px)`,
            transition: 'transform 0.1s ease-out',
            whiteSpace: 'nowrap',
            userSelect: 'none'
          }}>
            CATALOG
          </div>
        </div>

        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(rgba(10, 102, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(10, 102, 255, 0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          zIndex: 1,
          pointerEvents: 'none',
          transform: `translate(${mousePos.x * 20}px, ${mousePos.y * 20}px)`,
          transition: 'transform 0.15s ease-out',
        }}></div>

        <div className="hero-background-slider" style={{ zIndex: 2, transform: `translate(${mousePos.x * -30}px, ${mousePos.y * -30}px)`, transition: 'transform 0.2s ease-out' }}>
          <div className="full-slide active">
            <div className="bg-img-container" style={{ position: 'absolute', inset: 0 }}>
              <img src={aboutus} alt="Products Header" className="bg-img" style={{ objectPosition: 'right center' }} />
            </div>
            <div className="bg-overlay"></div>
          </div>
        </div>

        <div className="hero-content" style={{ zIndex: 10 }}>
          <button className="outline-button" style={{ marginBottom: '2rem' }} onClick={() => navigateTo('home')}>
            &#8592; Back to Home
          </button>
          <h1 className="hero-title">All Products</h1>
          <p className="hero-description">
            <strong style={{ color: "var(--accent-color)" }}>Professional-Grade Machinery</strong><br /><br />
            Browse our entire selection of professional-grade tools designed for industrial excellence.
          </p>
        </div>
      </section>

      <div style={{ paddingBottom: '8rem' }}>
        {categories.map((cat, catIdx) => {
          const catProducts = products.filter(p => p.categoryId === cat.id);
          if (catProducts.length === 0) return null;

          return (
            <section key={cat.id} id={cat.id} className="category-section">
              <div className="category-header animate-hidden" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  backgroundColor: 'rgba(10, 102, 255, 0.1)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid var(--border-color)',
                  marginBottom: '1.5rem'
                }}>
                  <FontAwesomeIcon icon={faCogs} style={{ color: 'var(--accent-color)', fontSize: '1.8rem' }} />
                </div>
                <h3 className="category-title">{cat.name}</h3>
                <p className="category-desc">{cat.description}</p>
              </div>

              <div className="products-grid">
                {catProducts.map((product, idx) => (
                  <ProductItemCard
                    key={product._id}
                    product={product}
                    idx={idx}
                    navigateTo={navigateTo}
                    setSelectedImage={setSelectedImage}
                  />
                ))}
              </div>

              {catIdx !== categories.length - 1 && (
                <div className="category-wave-separator animate-hidden delay-200" style={{ width: '100%', overflow: 'hidden' }}>
                  <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '100%', height: 'auto' }}>
                    <path
                      d="M0 60 C 360 120, 720 0, 1080 60 C 1260 90, 1380 90, 1440 60"
                      stroke="var(--accent-color)"
                      strokeWidth="2"
                      opacity="0.7"
                    />
                    <path
                      d="M0 60 C 360 120, 720 0, 1080 60 C 1260 90, 1380 90, 1440 60 L 1440 120 L 0 120 Z"
                      fill="rgba(10, 102, 255, 0.08)"
                    />
                  </svg>
                </div>
              )}
            </section>
          );
        })}
      </div>

      {selectedImage && (
        <div className="lightbox-overlay" onClick={() => setSelectedImage(null)}>
          <span className="lightbox-close" onClick={() => setSelectedImage(null)}>&times;</span>
          <img src={selectedImage} alt="Full Screen Item" className="lightbox-img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}